import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="menu"
// The overflow (⋮) menu in an entity card's footer — see EntityCardComponent.
//
// Not a <dialog>: that is modal and would steal focus and lock the page for a
// two-item menu. Not the native Popover API either: Safari below 17 ignores the
// `popover` attribute outright, and the panel would then render permanently
// open — a worse failure than the thirty lines below.
//
// Closing is handled at the document level (not by stopping propagation on the
// button) precisely so that clicking a *second* card's ⋮ closes the first one:
// that click is outside the first controller's element, so its listener fires,
// while the second sees the click as its own and stays open.
export default class extends Controller {
	static targets = ["panel", "button"];

	connect() {
		document.addEventListener("click", this.closeOnOutsideClick);
		document.addEventListener("keydown", this.closeOnEscape);
	}

	disconnect() {
		document.removeEventListener("click", this.closeOnOutsideClick);
		document.removeEventListener("keydown", this.closeOnEscape);
	}

	get isOpen() {
		return !this.panelTarget.hidden;
	}

	toggle() {
		if (this.isOpen) {
			this.close();
		} else {
			this.open();
		}
	}

	open() {
		this.panelTarget.hidden = false;
		this.buttonTarget.setAttribute("aria-expanded", "true");
	}

	close() {
		this.panelTarget.hidden = true;
		this.buttonTarget.setAttribute("aria-expanded", "false");
	}

	closeOnOutsideClick = (event) => {
		if (this.isOpen && !this.element.contains(event.target)) this.close();
	};

	// Return focus to the trigger: Escape with focus parked on a now-hidden menu
	// item would otherwise strand the keyboard user at the top of the document.
	closeOnEscape = (event) => {
		if (event.key !== "Escape" || !this.isOpen || event.target.closest?.("dialog")) return;
		this.close();
		this.buttonTarget.focus();
	};
}
